import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ClockIconComponent,
  ReportsIconComponent,
  RefreshIconComponent,
  StepDiagramIconComponent,
  TimerIconComponent,
} from '@kuiper/icons';
import { RequirementTabComponent } from './requirement-tab/requirement-tab.component';
import { HistoryTabComponent } from './history-tab/history-tab.component';
import { TranslocoService } from '@jsverse/transloco';
import { finalize } from 'rxjs';
import { requestDetailsEmptyData } from '../request.mockdata';
import { ReportsTabComponent } from './reports-tab/reports-tab.component';
import { StepDiagramTabComponent } from './step-diagram-tab/step-diagram-tab.component';
import { SchedulesTabComponent } from './schedules-tab/schedules-tab.component';
import { TabsComponent } from '../../../../../ui-components/lib/tabs/tabs.component';
import { TabComponent } from '../../../../../ui-components/lib/tabs/tab/tab.component';
import { RequestDetailsHeaderComponent } from '../../../../common/components/request-details-header/request-details-header.component';
import { RequestService } from '../../../../common/services/request/request.service';
import {
  RequestDetailsResponse,
  RequestRequirement,
} from '../../../../common/models/request-details.model';
import {
  StepsDiagramPosition,
  StepsDiagramTree,
} from '../../../../common/models/steps-diagram.model';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import {
  FLOATING_DETAILS_CONTENT_CLASSES,
  FLOATING_PAGE_CLASSES,
} from '../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import {
  EMPLOYEE_MAIN_ROUTE,
  EMPLOYEE_ROUTES,
} from '../../../../common/constants/employee.routing.constants';
import { TreeTag } from 'all-libraries/ibusiness-core/src/core/common/components/tree/tree-tags/tree-tags.component';

@Component({
  selector: 'lib-request-details',
  imports: [
    TabsComponent,
    TabComponent,
    RequirementTabComponent,
    RequestDetailsHeaderComponent,
    HistoryTabComponent,
    ReportsTabComponent,
    StepDiagramTabComponent,
    SchedulesTabComponent,
    ClockIconComponent,
    ReportsIconComponent,
    RefreshIconComponent,
    StepDiagramIconComponent,
    TimerIconComponent,
  ],
  templateUrl: './request-details.component.html',
  styleUrl: './request-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RequestDetailsComponent implements OnInit {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;
  readonly EMPLOYEE_MAIN_ROUTE = EMPLOYEE_MAIN_ROUTE;
  readonly DETAILS_TAB_PARAM_KEY = 'details-tab';
  private readonly featureFlagsService = inject(FeatureFlagsService);

  // Read directly (not via the list-page opt-in) so tables inside the tabs keep their current layout
  protected readonly systemTabs = computed(() =>
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.SYSTEM.system_tabs
    )
  );
  protected readonly floating = computed(() =>
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.SYSTEM.system_default_color
    )
  );
  protected readonly tabsContentClass = FLOATING_DETAILS_CONTENT_CLASSES;
  protected readonly floatingPageClasses = computed(() =>
    this.floating() ? FLOATING_PAGE_CLASSES : ''
  );

  private readonly destroyRef = inject(DestroyRef);
  requestService = inject(RequestService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  transloco = inject(TranslocoService);
  tenantService = inject(TenantService);
  translate = inject(TranslocoService);

  requestDetailsResponse = signal<RequestDetailsResponse>({
    data: requestDetailsEmptyData,
  });
  allStepsDiagramPositions = signal<StepsDiagramPosition[]>([]);
  stepsDiagramPositions = signal<StepsDiagramPosition[]>([]);
  requirements = signal<RequestRequirement[] | undefined>([]);
  subFlowTemplateExists = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  requestTags = signal<TreeTag[]>([]);
  firstRequestTags = signal<TreeTag>({
    label: this.translate.translate('invoice_page_table_filter_tab_all'),
    value: '',
    active: true,
  });

  visibleTabs = computed(() =>
    this.subFlowTemplateExists()
      ? this.tabs.filter(tab => tab.key !== 'steps-diagram')
      : this.tabs
  );

  showAddScheduleAction = computed(() =>
    this.requestService.showAddScheduleAction()
  );
  backPath = computed(() =>
    this.requestService.navigationPath(EMPLOYEE_ROUTES.REQUESTS.LIST)
  );

  tabs = this.requestService.detailsTabs;

  currentTabIndex = signal<number>(0);
  requestId = signal<string | undefined>(undefined);

  ngOnInit() {
    this.restoreTabFromUrl();
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.requestId.set(params['id']);
        this.getRequestSummary(params['id']);
        this.getStepsDiagramTree(params['id']);
      });
  }

  restoreTabFromUrl() {
    const tabParam =
      this.activatedRoute.snapshot.queryParams[this.DETAILS_TAB_PARAM_KEY];
    if (tabParam) {
      const tabIndex = this.tabs.findIndex(tab => tab.key === tabParam);
      if (tabIndex > -1) {
        this.currentTabIndex.set(tabIndex);
      }
    }
  }

  getRequestSummary(requestId: string) {
    this.isLoading.set(true);
    this.requestService
      .getRequestSummary(requestId)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response: RequestDetailsResponse) => {
        const details = this.requestService.mapSummaryDetailsResponse(response);
        this.requestDetailsResponse.set(details);
        this.requirements.set(this.requestDetailsResponse().data.requirements);
        this.subFlowTemplateExists.set(
          response.data.sub_flow_templates_exists ?? false
        );
      });
  }

  getStepsDiagramTree(requestId: string) {
    this.requestService
      .getStepsDiagramTree(requestId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: StepsDiagramTree) => {
        this.allStepsDiagramPositions.set(response.data);
        this.stepsDiagramPositions.set(response.data);
        this.getRequestTags();
      });
  }

  getRequestTags() {
    const uniqueTags = new Map<string, TreeTag>();

    for (const position of this.allStepsDiagramPositions()) {
      for (const step of position.steps ?? []) {
        for (const tag of step.tags ?? []) {
          if (!uniqueTags.has(tag.value)) {
            uniqueTags.set(tag.value, {
              label: tag.label,
              value: tag.value,
              active: false,
            });
          }
        }
      }
    }

    const tags = Array.from(uniqueTags.values());

    this.requestTags.set(tags.length ? [this.firstRequestTags(), ...tags] : []);
  }

  onTagsChanged(value: string) {
    if (!value) {
      this.stepsDiagramPositions.set(this.allStepsDiagramPositions());
      return;
    }

    this.stepsDiagramPositions.set(
      this.allStepsDiagramPositions()
        .map(position => ({
          ...position,
          steps: position.steps.filter(
            step =>
              step.tags.length === 0 ||
              step.tags.some(tag => tag.value === value)
          ),
        }))
        .filter(position => position.steps.length > 0)
    );
  }

  tabNavigation(tabIndex: number) {
    this.currentTabIndex.set(tabIndex);
  }
}
